import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  Paperclip,
  Download,
} from 'lucide-react'
import {
  supportInquiryApi,
  type InquiryStatus,
  type InquiryCategory,
  type AdminInquiryDetailResponse,
} from '@/api/supportInquiry'

const STATUS_OPTIONS: { value: InquiryStatus | ''; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'pending', label: '대기중' },
  { value: 'answered', label: '답변완료' },
  { value: 'closed', label: '종료' },
]

const CATEGORY_OPTIONS: { value: InquiryCategory | ''; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'learning', label: '학습' },
  { value: 'technical', label: '기술' },
  { value: 'account', label: '계정' },
  { value: 'other', label: '기타' },
]

const STATUS_BADGES: Record<InquiryStatus, { bg: string; text: string; label: string; icon: typeof Clock }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: '대기중', icon: Clock },
  answered: { bg: 'bg-green-100', text: 'text-green-700', label: '답변완료', icon: CheckCircle },
  closed: { bg: 'bg-slate-100', text: 'text-slate-700', label: '종료', icon: XCircle },
}

const CATEGORY_LABELS: Record<InquiryCategory, string> = {
  learning: '학습',
  technical: '기술',
  account: '계정',
  other: '기타',
}

function DetailModal({
  inquiryId,
  onClose,
}: {
  inquiryId: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [replyContent, setReplyContent] = useState('')

  const { data: detail, isLoading } = useQuery({
    queryKey: ['admin', 'support-inquiries', inquiryId],
    queryFn: () => supportInquiryApi.getById(inquiryId),
  })

  const replyMutation = useMutation({
    mutationFn: (content: string) => supportInquiryApi.createReply(inquiryId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries', inquiryId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries-list'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries-stats'] })
      setReplyContent('')
    },
  })

  const statusMutation = useMutation({
    mutationFn: (status: string) => supportInquiryApi.updateStatus(inquiryId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries', inquiryId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries-list'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries-stats'] })
    },
  })

  const handleSubmitReply = () => {
    if (!replyContent.trim()) return
    replyMutation.mutate(replyContent)
  }

  const handleStatusChange = (status: string) => {
    if (confirm(`문의 상태를 "${STATUS_BADGES[status as InquiryStatus]?.label}"로 변경하시겠습니까?`)) {
      statusMutation.mutate(status)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">문의 상세</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-slate-600 animate-spin" />
          </div>
        ) : detail ? (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Inquiry Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-600 mb-1">기관명</div>
                    <div className="text-slate-900">{detail.organizationName || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 mb-1">문의자</div>
                    <div className="text-slate-900">
                      {detail.userName || '-'} ({detail.userEmail || '-'})
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 mb-1">카테고리</div>
                    <div className="text-slate-900">
                      {CATEGORY_LABELS[detail.category as InquiryCategory] || detail.category}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 mb-1">상태</div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        STATUS_BADGES[detail.status as InquiryStatus]?.bg
                      } ${STATUS_BADGES[detail.status as InquiryStatus]?.text}`}
                    >
                      {STATUS_BADGES[detail.status as InquiryStatus]?.label || detail.status}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-slate-600 mb-1">문의일</div>
                  <div className="text-slate-900">
                    {new Date(detail.createdAt).toLocaleString('ko-KR')}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-slate-600 mb-2">제목</div>
                  <div className="text-lg font-semibold text-slate-900">{detail.title}</div>
                </div>

                <div>
                  <div className="text-sm text-slate-600 mb-2">내용</div>
                  <div className="bg-slate-50 rounded-lg p-4 whitespace-pre-wrap text-slate-900">
                    {detail.content}
                  </div>
                </div>

                {/* Attachments */}
                {detail.attachments.length > 0 && (
                  <div>
                    <div className="text-sm text-slate-600 mb-2">첨부파일</div>
                    <div className="space-y-2">
                      {detail.attachments.map((attachment) => (
                        <div
                          key={attachment.attachmentId}
                          className="flex items-center gap-2 bg-slate-50 rounded-lg p-3"
                        >
                          <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <span className="text-sm text-slate-900 flex-1">{attachment.fileName}</span>
                          <span className="text-xs text-slate-500">
                            {formatFileSize(attachment.fileSize)}
                          </span>
                          {attachment.downloadUrl && (
                            <a
                              href={attachment.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-slate-200 rounded text-blue-600"
                              title="다운로드"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Replies */}
              {detail.replies.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-900 mb-3">
                    답변 ({detail.replies.length})
                  </div>
                  <div className="space-y-3">
                    {detail.replies.map((reply) => (
                      <div key={reply.replyId} className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-slate-900">
                            {reply.authorName}
                          </span>
                          <span className="text-xs text-slate-500">({reply.authorRole})</span>
                          <span className="text-xs text-slate-500 ml-auto">
                            {new Date(reply.createdAt).toLocaleString('ko-KR')}
                          </span>
                        </div>
                        <div className="text-sm text-slate-900 whitespace-pre-wrap">
                          {reply.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply Form */}
              <div>
                <div className="text-sm font-semibold text-slate-900 mb-2">답변 작성</div>
                <textarea
                  className="input w-full min-h-[120px] resize-y"
                  placeholder="답변 내용을 입력하세요..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                {detail.status !== 'answered' && (
                  <button
                    onClick={() => handleStatusChange('answered')}
                    disabled={statusMutation.isPending}
                    className="btn-secondary btn-sm inline-flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    답변완료
                  </button>
                )}
                {detail.status !== 'closed' && (
                  <button
                    onClick={() => handleStatusChange('closed')}
                    disabled={statusMutation.isPending}
                    className="btn-secondary btn-sm inline-flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    종료
                  </button>
                )}
              </div>
              <button
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || replyMutation.isPending}
                className="btn-primary btn-sm inline-flex items-center gap-2"
              >
                {replyMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                답변 작성
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export function SupportInquiryPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filterStatus, setFilterStatus] = useState<InquiryStatus | ''>('')
  const [filterCategory, setFilterCategory] = useState<InquiryCategory | ''>('')
  const [selectedInquiryId, setSelectedInquiryId] = useState<number | null>(null)

  const { data: stats } = useQuery({
    queryKey: ['admin', 'support-inquiries-stats'],
    queryFn: supportInquiryApi.getStats,
    staleTime: 30 * 1000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'support-inquiries-list', { page, status: filterStatus, category: filterCategory }],
    queryFn: () =>
      supportInquiryApi.getList({
        page,
        size: 20,
        status: filterStatus || undefined,
        category: filterCategory || undefined,
      }),
    staleTime: 30 * 1000,
  })

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">문의 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-blue-600" />
            문의 관리
          </h1>
          <div className="flex items-center gap-3 mt-2">
            {stats && (
              <>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                  대기: {stats.pending}
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  답변완료: {stats.answered}
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
                  종료: {stats.closed}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            className="input w-40"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value as InquiryStatus | '')
              setPage(0)
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="input w-40"
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value as InquiryCategory | '')
              setPage(0)
            }}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">기관명</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">문의자</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">카테고리</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">제목</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">답변수</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">문의일</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((inquiry) => (
                <tr
                  key={inquiry.inquiryId}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedInquiryId(inquiry.inquiryId)}
                >
                  <td className="px-6 py-4 text-sm text-slate-900">
                    {inquiry.organizationName || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900">{inquiry.userName || '-'}</div>
                    <div className="text-xs text-slate-500">{inquiry.userEmail || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {CATEGORY_LABELS[inquiry.category as InquiryCategory] || inquiry.category}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-900 truncate max-w-[300px] block">
                      {inquiry.title}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        STATUS_BADGES[inquiry.status as InquiryStatus]?.bg
                      } ${STATUS_BADGES[inquiry.status as InquiryStatus]?.text}`}
                    >
                      {STATUS_BADGES[inquiry.status as InquiryStatus]?.label || inquiry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-center">
                    {inquiry.replyCount}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(inquiry.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                </tr>
              ))}
              {data?.content.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    문의가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} -{' '}
              {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages - 1}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInquiryId !== null && (
        <DetailModal
          inquiryId={selectedInquiryId}
          onClose={() => setSelectedInquiryId(null)}
        />
      )}
    </div>
  )
}
